import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownItem,
	DropdownSelectTrigger,
} from '../../components-v2/Dropdown';
import { type IDropdownOption, TRIGGER_TYPE_OPTIONS } from '../../containers/Automations/triggerTypeOptions';
import { brandPrimary, brandPrimaryHover, grayIconFill, inputBorderColor, white } from '../../styles/colors';
import { SearchIcon } from '../svgs/icons/SearchIcon';
import { TextInput } from '../TextInput';

interface ICreatorOption {
	title: string;
	value: string | null;
}

interface IStatusOption {
	label: string;
	value: string;
}

interface IAutomationFiltersProps {
	searchText: string;
	selectedCreatorId: string | null;
	creatorOptions: ICreatorOption[];
	selectedTriggerType: string;
	onSearchTextChange: (text: string) => void;
	onCreatorFilterChange: (creatorId: string | null) => void;
	onTriggerTypeChange: (triggerType: string) => void;
	// Optional status filter (for inactive tab)
	statusOptions?: IStatusOption[];
	selectedStatuses?: string[];
	onStatusChange?: (status: string) => void;
	// Optional resource selector filter (when trigger type is ResourceSelectorAutomationTrigger)
	selectedResourceSelectors?: string[];
	onResourceSelectorChange?: (resourceSelector: string) => void;
	// Dynamic options from API (falls back to static options when not provided)
	triggerTypeOptions?: IDropdownOption[];
	resourceSelectorOptions?: IDropdownOption[];
}

export const AutomationFilters: React.FC<IAutomationFiltersProps> = ({
	searchText,
	selectedCreatorId,
	creatorOptions,
	selectedTriggerType,
	onSearchTextChange,
	onCreatorFilterChange,
	onTriggerTypeChange,
	statusOptions,
	selectedStatuses,
	onStatusChange,
	selectedResourceSelectors,
	onResourceSelectorChange,
	triggerTypeOptions: triggerTypeOptionsProp,
	resourceSelectorOptions: resourceSelectorOptionsProp,
}) => {
	const resolvedTriggerTypeOptions = triggerTypeOptionsProp ?? TRIGGER_TYPE_OPTIONS;
	const resolvedResourceSelectorOptions = resourceSelectorOptionsProp ?? [];
	const selectedCreatorOption = creatorOptions.find(option => option.value === selectedCreatorId) || creatorOptions[0];

	const statusLabel = React.useMemo(() => {
		if (!statusOptions || !selectedStatuses) return '';
		if (selectedStatuses.length === statusOptions.length) return 'All Statuses';
		return selectedStatuses.map(s => statusOptions.find(o => o.value === s)?.label).join(', ');
	}, [statusOptions, selectedStatuses]);

	const resourceSelectorLabel = React.useMemo(() => {
		if (!selectedResourceSelectors) return '';
		if (selectedResourceSelectors.length === 0) return 'All Resource Selectors';
		if (selectedResourceSelectors.length === resolvedResourceSelectorOptions.length) return 'All Resource Selectors';
		return selectedResourceSelectors
			.map(s => resolvedResourceSelectorOptions.find(o => o.value === s)?.label)
			.join(', ');
	}, [selectedResourceSelectors, resolvedResourceSelectorOptions]);

	const showResourceSelectorFilter =
		selectedTriggerType === 'ResourceSelectorAutomationTrigger' && onResourceSelectorChange;

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.searchContainer)}>
				<TextInput
					type='text'
					inputId='search-automations'
					placeholder='Search by automation name...'
					value={searchText}
					onChange={e => onSearchTextChange(e.target.value)}
					leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} fillColor={grayIconFill} />}
					rightAccessory={
						searchText ? (
							<button type='button' className={css(styleSheet.clearButton)} onClick={() => onSearchTextChange('')}>
								Clear
							</button>
						) : null
					}
					className={css(styleSheet.searchInputWrapper)}
					inputClassName={css(styleSheet.searchInput)}
				/>
			</div>
			<div className={css(styleSheet.triggerTypeContainer)}>
				<Dropdown>
					<DropdownSelectTrigger styles={[styleSheet.dropdownTrigger]}>
						{resolvedTriggerTypeOptions.find(opt => opt.value === selectedTriggerType)?.label || 'All Triggers'}
					</DropdownSelectTrigger>
					<DropdownContent styles={[styleSheet.dropdownContent]}>
						{resolvedTriggerTypeOptions.map(option => (
							<DropdownItem
								key={option.value}
								onClick={() => onTriggerTypeChange(option.value)}
								styles={[styleSheet.dropdownItem]}
							>
								{option.label}
							</DropdownItem>
						))}
					</DropdownContent>
				</Dropdown>
			</div>
			{showResourceSelectorFilter && (
				<div className={css(styleSheet.resourceSelectorContainer)}>
					<Dropdown>
						<DropdownSelectTrigger styles={[styleSheet.dropdownTrigger]}>{resourceSelectorLabel}</DropdownSelectTrigger>
						<DropdownContent styles={[styleSheet.dropdownContent]}>
							{resolvedResourceSelectorOptions.map(option => (
								<DropdownCheckboxItem
									key={option.value}
									checked={selectedResourceSelectors?.includes(option.value) ?? false}
									onCheckedChange={() => onResourceSelectorChange?.(option.value)}
									closeOnClick={false}
									styles={[styleSheet.dropdownItem]}
								>
									{option.label}
								</DropdownCheckboxItem>
							))}
						</DropdownContent>
					</Dropdown>
				</div>
			)}
			<div className={css(styleSheet.creatorFilterContainer)}>
				<Dropdown>
					<DropdownSelectTrigger styles={[styleSheet.dropdownTrigger]}>
						{selectedCreatorOption?.title || 'Show all creators'}
					</DropdownSelectTrigger>
					<DropdownContent styles={[styleSheet.dropdownContent]}>
						{creatorOptions.map(option => (
							<DropdownItem
								key={option.value || 'all'}
								onClick={() => onCreatorFilterChange(option.value)}
								styles={[styleSheet.dropdownItem]}
							>
								{option.title}
							</DropdownItem>
						))}
					</DropdownContent>
				</Dropdown>
			</div>
			{statusOptions && selectedStatuses && onStatusChange && (
				<div className={css(styleSheet.statusFilterContainer)}>
					<Dropdown>
						<DropdownSelectTrigger styles={[styleSheet.dropdownTrigger]}>{statusLabel}</DropdownSelectTrigger>
						<DropdownContent styles={[styleSheet.dropdownContent]}>
							{statusOptions.map(option => (
								<DropdownCheckboxItem
									key={option.value}
									checked={selectedStatuses.includes(option.value)}
									onCheckedChange={() => onStatusChange(option.value)}
									closeOnClick={false}
									styles={[styleSheet.dropdownItem]}
								>
									{option.label}
								</DropdownCheckboxItem>
							))}
						</DropdownContent>
					</Dropdown>
				</div>
			)}
		</div>
	);
};

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '16px',
		alignItems: 'center',
		padding: '0',
		marginBottom: '0',
		justifyContent: 'flex-start',
	},
	searchContainer: {
		flex: '0 0 300px',
		width: '300px',
	},
	searchInputWrapper: {
		background: white,
		borderRadius: '6px',
		border: `1px solid ${inputBorderColor}`,
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		':hover': {
			borderColor: brandPrimary,
		},
	},
	searchInput: {
		paddingLeft: 8,
	},
	searchIcon: {
		width: '20px !important',
		height: '20px !important',
		marginRight: 8,
		flexShrink: 0,
	},
	clearButton: {
		background: 'none',
		border: 'none',
		color: brandPrimary,
		cursor: 'pointer',
		fontSize: '14px',
		fontWeight: 500,
		padding: '4px 12px 4px 8px',
		borderRadius: '4px',
		':hover': {
			fontWeight: 700,
		},
	},
	triggerTypeContainer: {
		flex: '0 0 auto',
		minWidth: '180px',
	},
	resourceSelectorContainer: {
		flex: '0 0 auto',
		minWidth: '200px',
	},
	creatorFilterContainer: {
		flex: '0 0 auto',
		minWidth: '200px',
	},
	statusFilterContainer: {
		flex: '0 0 auto',
		minWidth: '150px',
	},
	dropdownTrigger: {
		height: '40px',
		width: '100%',
		borderRadius: '6px',
		border: `1px solid ${inputBorderColor}`,
		backgroundColor: white,
		padding: '12px',
		fontSize: '14px',
		color: '#4A4A4A',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		':hover': {
			borderColor: brandPrimary,
		},
	},
	dropdownContent: {
		maxHeight: '300px',
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	dropdownItem: {
		padding: '8px 12px',
		fontSize: '14px',
		color: '#4A4A4A',
		cursor: 'pointer',
		':hover': {
			backgroundColor: brandPrimaryHover,
			color: white,
		},
	},
});
